import { useState, useEffect } from 'react';
import { Settings } from 'lucide-react';
import api from '../services/api';

interface AdminSettings {
    id: number;
    travel_rate: number;
    hourly_rate: number;
    last_job_number?: number;
}

export default function SettingsPage() {
    const [settings, setSettings] = useState<AdminSettings | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const response = await api.get('/admin/settings');
            setSettings(response.data.data);
        } catch (error) {
            setError('Failed to fetch settings');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            setError(null);
            setSuccess(null);

            const formData = new FormData(e.currentTarget);
            const data = {
                travel_rate: parseFloat(formData.get('travel_rate') as string),
                hourly_rate: parseFloat(formData.get('hourly_rate') as string)
            };

            await api.put('/admin/settings', data);
            setSuccess('Settings updated successfully');
            await fetchSettings();
        } catch (error) {
            setError('Failed to update settings');
            console.error(error);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-gray-500">Loading settings...</div>
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto">
            <div className="mb-8">
                <div className="flex items-center gap-3">
                    <Settings className="w-8 h-8 text-gray-700 dark:text-gray-300" />
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Settings</h1>
                        <p className="text-gray-600 dark:text-gray-400">Manage application settings</p>
                    </div>
                </div>
            </div>

            {error && (
                <div className="mb-4 p-4 text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/50 rounded-lg">
                    {error}
                </div>
            )}

            {success && (
                <div className="mb-4 p-4 text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/50 rounded-lg">
                    {success}
                </div>
            )}

            <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Most recent generated job number</p>
                        <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                            {settings?.last_job_number !== undefined
                                ? `IS${String(settings.last_job_number).padStart(6, '0')}`
                                : '–'}
                        </p>
                    </div>
                    <div className="text-xs text-gray-400 dark:text-gray-500">
                        Read-only • managed automatically via job card sequence
                    </div>
                </div>
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    <div>
                        <label htmlFor="travel_rate" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Travel Rate
                        </label>
                        <div className="mt-1 relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <span className="text-gray-500 dark:text-gray-400 sm:text-sm">R</span>
                            </div>
                            <input
                                type="number"
                                name="travel_rate"
                                id="travel_rate"
                                step="0.01"
                                defaultValue={settings?.travel_rate}
                                className="block w-full pl-7 pr-12 sm:text-sm border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                placeholder="0.00"
                            />
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                <span className="text-gray-500 dark:text-gray-400 sm:text-sm">/km</span>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label htmlFor="hourly_rate" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Hourly Rate
                        </label>
                        <div className="mt-1 relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <span className="text-gray-500 dark:text-gray-400 sm:text-sm">R</span>
                            </div>
                            <input
                                type="number"
                                name="hourly_rate"
                                id="hourly_rate"
                                step="0.01"
                                defaultValue={settings?.hourly_rate}
                                className="block w-full pl-7 pr-12 sm:text-sm border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                placeholder="0.00"
                            />
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                <span className="text-gray-500 dark:text-gray-400 sm:text-sm">/hr</span>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600"
                        >
                            Save Changes
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
} 